import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  ticketId: string;
  onSubmitted?: () => void;
}

const SupportReviewDialog = ({ open, onOpenChange, ticketId, onSubmitted }: Props) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user) return;
    if (rating < 1) {
      toast.error("Sélectionnez une note");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("support_reviews").insert({
      ticket_id: ticketId,
      user_id: user.id,
      rating,
      comment: comment.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      console.error(error);
      toast.error("Échec de l'envoi — peut-être déjà évalué");
      return;
    }
    toast.success("Merci ! Votre avis est publié dans la rubrique « Avis support technique ».");
    onSubmitted?.();
    onOpenChange(false);
    setRating(0);
    setComment("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>⭐ Évaluez votre échange support</DialogTitle>
          <DialogDescription>
            Votre note (1 à 5 étoiles) et votre commentaire seront publiés sur la page <strong>Avis support technique</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                className="p-1 transition-transform hover:scale-110"
                aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
              >
                <Star
                  className={`w-10 h-10 ${
                    (hover || rating) >= n ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40"
                  }`}
                />
              </button>
            ))}
          </div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Décrivez votre expérience (optionnel)…"
            rows={4}
            maxLength={1000}
          />
          <p className="text-xs text-muted-foreground text-right">{comment.length}/1000</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Plus tard
            </Button>
            <Button onClick={submit} disabled={submitting || rating < 1}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
              Publier l'avis
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupportReviewDialog;
